import { BeaconClient, buildGeo } from "@beacon/client-core";
import type { MatcherClient } from "../../platform/matcher-transport.js";

const utf8 = (s: string) => new TextEncoder().encode(s);

export interface CreateInput { need: string; headline: string; details?: string; urgency: number; people?: number; cell: string }
export interface OfferInput { requestId: string; tags: string[]; etaMinutes?: number; note?: string; cell: string }
export interface AcceptInput { requestId: string; offerId: string; helperDid: string; preciseLocation: string; cell: string }
export interface AvailabilityInput { tags: string[]; cells: string[]; untilIso: string; autoAccept?: boolean; cell: string }
export interface TransitionInput { requestId: string; to: string; reason?: string; cell: string }
export interface NoteInput { requestId: string; recipients: string[]; text: string; cell: string }

/** Create a help request. Headline/details are sealed broadcast (ciphertext-on-wire, no-roadblock #3). */
export async function publishHelpRequest(client: BeaconClient, input: CreateInput) {
  const payload: Record<string, unknown> = {
    need: input.need, urgency: input.urgency,
    headline: await client.sealBroadcast(utf8(input.headline)),
  };
  if (input.details) payload.details = await client.sealBroadcast(utf8(input.details));
  if (input.people !== undefined) payload.people = input.people;
  return client.publish({ $type: "app.aid.helpRequest.create", payload, geo: buildGeo(input.cell) });
}

/** Offer help. COARSE/cell location only — NO precise location either way (matching §4). */
export async function publishOffer(client: BeaconClient, input: OfferInput) {
  const payload: Record<string, unknown> = { tags: input.tags };
  if (input.etaMinutes !== undefined) payload.etaMinutes = input.etaMinutes;
  if (input.note) payload.note = await client.sealBroadcast(utf8(input.note));
  return client.publish({ $type: "app.aid.helpRequest.offer", payload, geo: buildGeo(input.cell), subject: input.requestId });
}

/** Accept an offer — THE privacy keystone. Releases precise location need-to-know to the one
 *  accepted helper, scoped by subjectTid = requestId (Privacy §4.2). */
export async function publishAccept(client: BeaconClient, input: AcceptInput) {
  const locationGrant = await client.sealNeedToKnow([input.helperDid], input.requestId, utf8(input.preciseLocation));
  return client.publish({
    $type: "app.aid.helpRequest.accept",
    payload: { offer: input.offerId, locationGrant },
    geo: buildGeo(input.cell), subject: input.requestId,
  });
}

/** Register helper availability. PRIVATE: signed but never published to the public
 *  firehose — the signed envelope is POSTed straight to the matcher (A7), which is
 *  the only consumer (services/matcher/server.go `/availability`). cells are res-7. */
export async function publishAvailability(client: BeaconClient, matcher: MatcherClient, input: AvailabilityInput) {
  const payload: Record<string, unknown> = { tags: input.tags, cells: input.cells, until: input.untilIso };
  if (input.autoAccept !== undefined) payload.autoAccept = input.autoAccept;
  const envelope = await client.sign({ $type: "app.aid.helper.availability", payload, geo: buildGeo(input.cell) });
  const ack = await matcher.postAvailability(envelope.wire.bytes);
  return { ...ack, id: envelope.wire.id, envelope };
}

/** Lifecycle transition (matching §7). */
export async function publishTransition(client: BeaconClient, input: TransitionInput) {
  const payload: Record<string, unknown> = { to: input.to };
  if (input.reason) payload.reason = await client.sealBroadcast(utf8(input.reason));
  return client.publish({ $type: "app.aid.helpRequest.transition", payload, geo: buildGeo(input.cell), subject: input.requestId });
}

/** Coordination-thread note — need-to-know to participants, scoped by requestId (matching §6). */
export async function publishCoordinationNote(client: BeaconClient, input: NoteInput) {
  const body = await client.sealNeedToKnow(input.recipients, input.requestId, utf8(input.text));
  return client.publish({ $type: "app.aid.helpRequest.note", payload: { body }, geo: buildGeo(input.cell), subject: input.requestId });
}
